<template>
  <div class="batch-upload-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>批量上传图片作业</span>
      </div>
      <el-form :model="uploadForm" :rules="rules" ref="uploadForm" label-width="100px">
        <el-form-item label="作业标题" prop="title">
          <el-input v-model="uploadForm.title" placeholder="请输入作业标题"></el-input>
        </el-form-item>
        <el-form-item label="科目" prop="subject">
          <el-select v-model="uploadForm.subject" placeholder="请选择科目">
            <el-option v-for="item in subjectOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="班级" prop="gradeId">
          <el-select v-model="uploadForm.gradeId" placeholder="请选择班级">
            <el-option v-for="grade in gradeList" :key="grade.id" :label="grade.name" :value="grade.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="截止日期" prop="dueDate">
          <el-date-picker
            v-model="uploadForm.dueDate"
            type="datetime"
            placeholder="选择截止日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="作业说明" prop="description">
          <el-input type="textarea" v-model="uploadForm.description" placeholder="请输入作业说明"></el-input>
        </el-form-item>
        <el-form-item label="上传方式" prop="uploadType">
          <el-radio-group v-model="uploadForm.uploadType">
            <el-radio :label="1">批量上传</el-radio>
            <el-radio :label="2">压缩包上传</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 批量上传模式 -->
        <el-form-item v-if="uploadForm.uploadType === 1" label="批量图片">
          <el-upload
            action="#"
            list-type="picture-card"
            :auto-upload="false"
            :file-list="fileList"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :multiple="true">
            <i class="el-icon-plus"></i>
          </el-upload>
        </el-form-item>

        <!-- 压缩包上传模式 -->
        <el-form-item v-if="uploadForm.uploadType === 2" label="压缩包">
          <el-upload
            class="upload-demo"
            drag
            action="#"
            :auto-upload="false"
            :file-list="zipFileList"
            :on-change="handleZipChange"
            :on-remove="handleZipRemove"
            :multiple="false"
            :limit="1"
            :before-upload="validateZipFile"
            accept=".zip">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将压缩包拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip" slot="tip">只能上传ZIP格式压缩文件，大小不超过50MB</div>
            <div class="el-upload__tip" slot="tip">
              <b>ZIP文件要求：</b>
              <ul>
                <li>图片文件必须在根目录下，不支持嵌套文件夹</li>
                <li>仅支持JPG、JPEG、PNG、GIF、BMP格式的图片</li>
                <li>请使用标准的ZIP压缩格式</li>
              </ul>
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="submitUpload" :loading="uploading">开始上传</el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 已上传作业列表 -->
    <el-card class="box-card" style="margin-top: 20px">
      <div slot="header" class="clearfix">
        <span>已上传批量作业</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="loadHomeworkList">刷新</el-button>
      </div>

      <el-table :data="homeworkList" v-loading="loading" style="width: 100%">
        <el-table-column prop="name" label="作业标题"></el-table-column>
        <el-table-column prop="subject" label="科目"></el-table-column>
        <el-table-column prop="gradeName" label="班级"></el-table-column>
        <el-table-column prop="fileCount" label="文件数量"></el-table-column>
        <el-table-column prop="dueDate" label="截止日期">
          <template slot-scope="scope">
            <span v-if="scope.row.dueDate">{{ formatDate(scope.row.dueDate) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间">
          <template slot-scope="scope">
            <span v-if="scope.row.createTime">{{ formatDate(scope.row.createTime) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="published" label="状态">
          <template slot-scope="scope">
            <el-tag :type="scope.row.published ? 'success' : 'info'">
              {{ scope.row.published ? '已发布' : '未发布' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220">
          <template slot-scope="scope">
            <el-button size="mini" @click="viewHomework(scope.row)">查看</el-button>
            <el-button size="mini" type="primary" @click="publishHomework(scope.row)" v-if="!scope.row.published">发布</el-button>
            <el-button size="mini" type="danger" @click="deleteHomework(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 查看作业详情对话框 -->
    <el-dialog title="作业详情" :visible.sync="dialogVisible" width="70%">
      <div v-if="currentHomework">
        <h3>{{ currentHomework.name }} <el-tag>{{ currentHomework.subject }}</el-tag></h3>
        <p>班级：{{ currentHomework.gradeName }}</p>
        <p>截止日期：<span v-if="currentHomework.dueDate">{{ formatDate(currentHomework.dueDate) }}</span><span v-else>-</span></p>
        <p>描述：{{ currentHomework.description || '-' }}</p>

        <el-divider content-position="left">作业图片列表</el-divider>

        <div class="image-gallery">
          <div v-for="(image, index) in homeworkImages" :key="index" class="image-item">
            <el-image
              :src="image.url"
              :preview-src-list="[image.url]"
              fit="cover">
            </el-image>
            <div class="image-name">{{ image.name }}</div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getBatchHomeworkList, uploadBatchHomework, getBatchHomeworkDetail, publishBatchHomework, deleteBatchHomework } from '@/api/homework'
import { getGradeList } from '@/api/grade'
import { formatDateTime, formatJavaLocalDateTime } from '@/utils/index'

export default {
  name: 'BatchUpload',
  data() {
    return {
      uploadForm: {
        title: '',
        subject: '',
        gradeId: '',
        dueDate: '',
        description: '',
        uploadType: 1 // 1-批量上传 2-压缩包上传
      },
      rules: {
        title: [{ required: true, message: '请输入作业标题', trigger: 'blur' }],
        subject: [{ required: true, message: '请选择科目', trigger: 'change' }],
        gradeId: [{ required: true, message: '请选择班级', trigger: 'change' }],
        dueDate: [{ required: true, message: '请选择截止日期', trigger: 'change' }]
      },
      subjectOptions: [
        { value: '语文', label: '语文' },
        { value: '数学', label: '数学' },
        { value: '英语', label: '英语' },
        { value: '物理', label: '物理' },
        { value: '化学', label: '化学' },
        { value: '生物', label: '生物' },
        { value: '政治', label: '政治' },
        { value: '历史', label: '历史' },
        { value: '地理', label: '地理' }
      ],
      gradeList: [],
      fileList: [],
      zipFileList: [],
      uploading: false,
      homeworkList: [],
      loading: false,
      dialogVisible: false,
      currentHomework: null,
      homeworkImages: []
    }
  },
  created() {
    this.loadGradeList()
    this.loadHomeworkList()
  },
  methods: {
    // 加载班级列表
    async loadGradeList() {
      try {
        const response = await getGradeList()
        if (response.code === 1) {
          this.gradeList = response.data
        } else {
          this.$message.error(response.msg || '获取班级列表失败')
        }
      } catch (error) {
        console.error('获取班级列表出错:', error)
        this.$message.error('获取班级列表失败')
      }
    },

    // 加载作业列表
    async loadHomeworkList() {
      this.loading = true;
      try {
        const response = await getBatchHomeworkList();
        console.log('原始API响应:', response);

        if (response.code === 1) {
          if (response.data && response.data.length > 0) {
            const firstItem = response.data[0];
            console.log('第一个作业项目:', firstItem);
            console.log('截止日期类型:', typeof firstItem.dueDate, '值:', firstItem.dueDate);
            console.log('创建时间类型:', typeof firstItem.createTime, '值:', firstItem.createTime);

            // 尝试直接解析日期
            if (firstItem.dueDate) {
              try {
                console.log('尝试解析截止日期:', new Date(firstItem.dueDate).toString());
              } catch (e) {
                console.error('截止日期解析失败:', e);
              }
            }

            if (firstItem.createTime) {
              try {
                console.log('尝试解析创建时间:', new Date(firstItem.createTime).toString());
              } catch (e) {
                console.error('创建时间解析失败:', e);
              }
            }
          }

          // 转换日期格式 - 使用最直接的方法
          this.homeworkList = response.data.map(item => {
            // 尝试直接格式化日期
            let formattedDueDate = '-';
            let formattedCreateTime = '-';

            try {
              if (item.dueDate) {
                const dueDate = new Date(item.dueDate);
                if (!isNaN(dueDate.getTime())) {
                  formattedDueDate = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')} ${String(dueDate.getHours()).padStart(2, '0')}:${String(dueDate.getMinutes()).padStart(2, '0')}:${String(dueDate.getSeconds()).padStart(2, '0')}`;
                }
              }
            } catch (e) {
              console.error('格式化截止日期出错:', e);
            }

            try {
              if (item.createTime) {
                const createTime = new Date(item.createTime);
                if (!isNaN(createTime.getTime())) {
                  formattedCreateTime = `${createTime.getFullYear()}-${String(createTime.getMonth() + 1).padStart(2, '0')}-${String(createTime.getDate()).padStart(2, '0')} ${String(createTime.getHours()).padStart(2, '0')}:${String(createTime.getMinutes()).padStart(2, '0')}:${String(createTime.getSeconds()).padStart(2, '0')}`;
                }
              }
            } catch (e) {
              console.error('格式化创建时间出错:', e);
            }

            return {
              ...item,
              originalDueDate: item.dueDate, // 保存原始日期值以便调试
              originalCreateTime: item.createTime,
              dueDate: formattedDueDate,
              createTime: formattedCreateTime
            };
          });

          console.log('处理后的作业列表:', this.homeworkList);
        } else {
          this.$message.error(response.msg || '获取作业列表失败');
        }
      } catch (error) {
        console.error('获取作业列表出错:', error);
        this.$message.error('获取作业列表失败');
      } finally {
        this.loading = false;
      }
    },

    // 处理文件变更
    handleFileChange(file, fileList) {
      this.fileList = fileList
    },

    // 处理文件移除
    handleFileRemove(file, fileList) {
      this.fileList = fileList
    },

    // 处理压缩包变更
    handleZipChange(file, fileList) {
      this.zipFileList = fileList;
    },

    // 处理压缩包移除
    handleZipRemove(file, fileList) {
      this.zipFileList = fileList;
    },

    // 提交上传
    submitUpload() {
      this.$refs.uploadForm.validate(async valid => {
        if (!valid) {
          return false;
        }

        // 检查是否有文件上传
        if (this.uploadForm.uploadType === 1 && this.fileList.length === 0) {
          this.$message.warning('请至少上传一张图片');
          return;
        }

        if (this.uploadForm.uploadType === 2 && this.zipFileList.length === 0) {
          this.$message.warning('请上传压缩包');
          return;
        }

        this.uploading = true;
        try {
          // 准备表单数据
          const formData = new FormData();
          formData.append('title', this.uploadForm.title);
          formData.append('subject', this.uploadForm.subject);
          formData.append('gradeId', this.uploadForm.gradeId);
          formData.append('dueDate', this.uploadForm.dueDate);
          formData.append('description', this.uploadForm.description || '');
          formData.append('uploadType', this.uploadForm.uploadType);

          // 根据不同的上传类型添加文件
          if (this.uploadForm.uploadType === 1) {
            // 批量上传
            this.fileList.forEach(file => {
              if (file != null && file.raw) {
                formData.append('files', file.raw);
              }
            });
          } else {
            // 压缩包上传
            if (this.zipFileList.length > 0 && this.zipFileList[0].raw) {
              formData.append('zipFile', this.zipFileList[0].raw);
            }
          }

          // 调用上传API
          const response = await uploadBatchHomework(formData);

          if (response.code === 1) {
            this.$message.success('上传成功');
            this.resetForm();
            this.loadHomeworkList();
          } else {
            this.$message.error(response.msg || '上传失败');
          }
        } catch (error) {
          console.error('上传出错:', error);
          if (error.message && error.message.includes('Network Error')) {
            this.$message.error('网络错误或服务器连接失败，请检查网络连接或联系管理员');
          } else {
            this.$message.error('上传失败，请检查文件格式和大小，或稍后重试');
          }
        } finally {
          this.uploading = false;
        }
      });
    },

    // 重置表单
    resetForm() {
      this.$refs.uploadForm.resetFields()
      this.fileList = []
      this.zipFileList = []
    },

    // 查看作业详情
    async viewHomework(row) {
      try {
        const response = await getBatchHomeworkDetail(row.id);
        console.log('作业详情原始响应:', response);

        if (response.code === 1) {
          const detailData = response.data;

          // 尝试格式化日期
          let formattedDueDate = '-';

          try {
            if (detailData.dueDate) {
              const dueDate = new Date(detailData.dueDate);
              if (!isNaN(dueDate.getTime())) {
                formattedDueDate = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')} ${String(dueDate.getHours()).padStart(2, '0')}:${String(dueDate.getMinutes()).padStart(2, '0')}:${String(dueDate.getSeconds()).padStart(2, '0')}`;
              } else {
                console.warn('作业详情中的截止日期无效:', detailData.dueDate);
              }
            }
          } catch (e) {
            console.error('格式化作业详情截止日期出错:', e);
          }

          // 处理后的数据
          this.currentHomework = {
            ...detailData,
            originalDueDate: detailData.dueDate, // 保存原始值
            dueDate: formattedDueDate
          };

          this.homeworkImages = detailData.images || [];
          this.dialogVisible = true;

          console.log('处理后的作业详情:', this.currentHomework);
        } else {
          this.$message.error(response.msg || '获取作业详情失败');
        }
      } catch (error) {
        console.error('获取作业详情出错:', error);
        this.$message.error('获取作业详情失败');
      }
    },

    // 发布作业
    async publishHomework(row) {
      try {
        this.$confirm('确定发布该批量作业吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(async () => {
          const response = await publishBatchHomework(row.id)
          if (response.code === 1) {
            this.$message.success('发布成功')
            this.loadHomeworkList()
          } else {
            this.$message.error(response.msg || '发布失败')
          }
        }).catch(() => {})
      } catch (error) {
        console.error('发布作业出错:', error)
        this.$message.error('发布失败')
      }
    },

    // 删除作业
    async deleteHomework(row) {
      try {
        this.$confirm('确定删除该批量作业吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(async () => {
          const response = await deleteBatchHomework(row.id)
          if (response.code === 1) {
            this.$message.success('删除成功')
            this.loadHomeworkList()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        }).catch(() => {})
      } catch (error) {
        console.error('删除作业出错:', error)
        this.$message.error('删除失败')
      }
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '-';

      // 首先尝试Java LocalDateTime格式
      try {
        const formatted = formatJavaLocalDateTime(date);
        if (formatted && formatted !== String(date)) {
          return formatted;
        }
      } catch (e) {
        console.error('Java日期格式化失败:', e);
      }

      // 尝试标准日期格式
      try {
        const jsDate = new Date(date);
        if (!isNaN(jsDate.getTime())) {
          return `${jsDate.getFullYear()}-${String(jsDate.getMonth() + 1).padStart(2, '0')}-${String(jsDate.getDate()).padStart(2, '0')} ${String(jsDate.getHours()).padStart(2, '0')}:${String(jsDate.getMinutes()).padStart(2, '0')}:${String(jsDate.getSeconds()).padStart(2, '0')}`;
        }
      } catch (e) {
        console.error('标准日期格式化失败:', e);
      }

      // 如果以上都失败，返回原始值或短横线
      return typeof date === 'string' ? date : '-';
    },

    // 验证压缩包文件
    validateZipFile(file) {
      const isZip = file.name.toLowerCase().endsWith('.zip');
      if (!isZip) {
        this.$message.error('只能上传ZIP格式的压缩包');
        return false;
      }
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        this.$message.error('文件大小不能超过50MB');
        return false;
      }
      return true;
    }
  }
}
</script>

<style scoped>
.batch-upload-container {
  padding: 20px;
}
.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}
.image-item {
  width: 200px;
}
.image-item .el-image {
  width: 100%;
  height: 150px;
}
.image-name {
  margin-top: 5px;
  font-size: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
